/*
 * Bon Pet V3 PDP bottom-section post-processor.
 *
 * Runs on every product page where `sections/page.liquid` rendered the
 * .bonpet-pdp-pane structure. Transforms the rte content into the
 * sidebar + section-anchored card-grid layout from the design without
 * touching the underlying Shopify Page record copy.
 *
 * Three responsibilities:
 *   1. Slug + id every h1 inside the rte. Populate the empty
 *      .bonpet-pdp-toc-list with one link per h1.
 *   2. Inside the FIRST h1 section ("Why we choose <protein>"), wrap
 *      every h2 + p + p triple in a .bonpet-pdp-card, then group those
 *      cards under a single .bonpet-pdp-card-grid container. Lift the
 *      leading emoji from each h2 into a .bonpet-pdp-card-icon span.
 *   3. Smooth-scroll the page on link click + toggle .is-active on the
 *      matching link as the corresponding section enters the upper-
 *      third of the viewport (IntersectionObserver scroll-spy).
 *
 * No-ops gracefully when the rte / toc isn't in the DOM, when the rte
 * has zero h1s, or when IntersectionObserver / scrollIntoView aren't
 * available (older browsers fall back to native anchor jumps).
 */
(function () {
  'use strict';

  var TOC_SELECTOR = '.bonpet-pdp-toc-list';
  var PANE_SELECTOR = '.bonpet-pdp-pane';
  var RTE_SELECTOR = '.bonpet-pdp-pane .rte';
  var EMOJI_RE = /^([\u{1F1E6}-\u{1F1FF}\u{1F300}-\u{1F5FF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]+️?)\s*/u;

  function slugify(text) {
    return String(text || '')
      .toLowerCase()
      .replace(EMOJI_RE, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'section';
  }

  function ensureUniqueId(base, used) {
    var id = 'bonpet-toc-' + base;
    var n = 2;
    while (used[id]) {
      id = 'bonpet-toc-' + base + '-' + n;
      n += 1;
    }
    used[id] = true;
    return id;
  }

  function splitEmoji(text) {
    var raw = String(text || '');
    var m = raw.match(EMOJI_RE);
    if (!m) return { emoji: '', rest: raw.trim() };
    return { emoji: m[1], rest: raw.slice(m[0].length).trim() };
  }

  /* Generic in-DOM heading replace. Copies innerHTML + attributes from
   * `src` into a freshly built element with `tag` and swaps it in. */
  function rewriteHeading(src, tag) {
    var rep = document.createElement(tag);
    rep.innerHTML = src.innerHTML;
    Array.prototype.slice.call(src.attributes).forEach(function (attr) {
      try { rep.setAttribute(attr.name, attr.value); } catch (e) { /* ignore */ }
    });
    src.parentNode.replaceChild(rep, src);
    return rep;
  }

  /* Some page records (e.g. cat-beef) author the reason headings as H3
   * instead of H2 - probably a hand-edit slip in the page record. The
   * card-wrap pipeline only recognises H2 reason headings, so normalise
   * top-level H3s to H2s here. Scoped to direct children of the rte so
   * we don't touch nested H3s inside tables / nav-lists. */
  function normalizeReasonHeadings(rte) {
    var h3s = rte.querySelectorAll(':scope > h3');
    Array.prototype.slice.call(h3s).forEach(function (h3) {
      rewriteHeading(h3, 'h2');
    });
  }

  /* Some page records (e.g. the fish PDP) author the lead heading as
   * <h2> instead of <h1>, leaving the page without a top-level H1 the
   * sidebar TOC can latch onto. Detect that shape and promote the
   * leading H2 to an H1 so the rest of the pipeline finds it. We only
   * promote when the very first heading among direct children is an
   * H2 - if an H1 appears earlier we trust the source. */
  function promoteLeadingH2(rte) {
    var first = rte.firstElementChild;
    while (first && first.tagName !== 'H1' && first.tagName !== 'H2') {
      first = first.nextElementSibling;
    }
    if (!first || first.tagName !== 'H2') return;
    rewriteHeading(first, 'h1');
  }

  function tagH1sAndBuildToc(rte, tocList) {
    var h1s = rte.querySelectorAll(':scope > h1');
    if (!h1s.length) return [];

    var used = {};
    var entries = [];

    h1s.forEach(function (h1) {
      var label = (h1.textContent || '').trim();
      if (!label) return;
      var slug = slugify(label);
      var id = ensureUniqueId(slug, used);
      h1.id = id;
      entries.push({ id: id, label: label, el: h1 });
    });

    return entries;
  }

  /* Derive a short chip label from a full heading. Strips leading emoji
   * and applies a few patterns:
   *   - "Why We Choose Brazilian Beef" -> "Why Brazilian"
   *   - "Benefits of ingredients"      -> "Benefits"
   *   - "Ingredients Ratio"            -> "Ratio"
   *   - "Transition Guide"             -> "Transition"
   *   - "Reviews"                      -> "Reviews"
   *   - Anything else: first 2 words after stripping emoji.
   * Falls back to the full label if no pattern matches and the text is
   * already short enough.
   */
  function shortLabel(text) {
    var t = String(text || '');
    t = t.replace(EMOJI_RE, '').trim();
    if (/^Why\s+We\s+Choose/i.test(t)) {
      // "Why We Choose Brazilian Pork" -> "Why Brazilian Pork"
      // Keep the trailing meat word so the first chip carries enough
      // context to identify the protein at a glance.
      return t.replace(/^Why\s+We\s+Choose\s+/i, 'Why ');
    }
    if (/Benefits/i.test(t)) return 'Benefits';
    if (/Ratio/i.test(t)) return 'Ratio';
    if (/Transition/i.test(t)) return 'Transition';
    if (/Review/i.test(t)) return 'Reviews';
    var words = t.split(/\s+/);
    if (words.length <= 2) return t;
    return words.slice(0, 2).join(' ');
  }

  /* Walk an element up to its enclosing Shopify section wrapper so we
   * can use that wrapper as a scroll target. Falls back to the original
   * element if no shopify-section ancestor is found. */
  function climbToShopifySection(el) {
    var n = el;
    while (n && n.parentNode && !/^shopify-section/.test(n.id || '')) {
      n = n.parentNode;
      if (n === document.body) return el;
    }
    return n;
  }

  /* Judge.me renders the Photos + Reviews widget containers on EVERY
   * product regardless of content, so the container existing doesn't
   * mean the section has anything to show. These two helpers read the
   * server-rendered counts so we can skip a pill when its section is
   * empty. Both are conservative: a missing / unparseable signal returns
   * null (treated as "not empty" -> keep the pill), so we never hide a
   * pill for a section that actually has content. */

  /* Number of reviews for THIS product, from the preview badge that
   * Judge.me renders in the product header (data-number-of-reviews).
   * Returns null if the badge isn't found. */
  function reviewCount() {
    var badge = document.querySelector('.jdgm-prev-badge[data-number-of-reviews]');
    if (!badge) return null;
    var n = parseInt(badge.getAttribute('data-number-of-reviews'), 10);
    return isNaN(n) ? null : n;
  }

  /* Number of items in the UGC media grid. NOTE: this grid is a
   * shop-wide media feed (the same data-json renders on every product),
   * so this is only 0 when the whole shop has no UGC media, not per
   * product. Returns null if the data element isn't found / parseable. */
  function ugcMediaCount() {
    var data = document.querySelector('.jdgm-ugc-media-data[data-json]');
    if (!data) return null;
    try {
      var arr = JSON.parse(data.getAttribute('data-json') || '');
      return Array.isArray(arr) ? arr.length : null;
    } catch (e) {
      return null;
    }
  }

  /* Walk the rest of the PDP DOM and add entries for the Judge.me
   * blocks further down the page: UGC media grid (Photos) then the
   * review widget (Reviews). Each block lives in its own Shopify
   * `apps_*` section in product-specific JSON templates. Transition
   * Guide is intentionally NOT added: that section belongs to the top
   * of the PDP (inside the product description) and shouldn't be
   * duplicated in the page-section sidebar / mobile chip rail.
   *
   * A pill is skipped when its section is empty (no photos / no
   * reviews) so the sidebar never points at an empty section. */
  function appendExtraEntries(entries, used) {
    var mediaEl = document.querySelector(
      '.jdgm-ugc-media-wrapper, [id*="ugc_media_grid"], .jdgm-pictures-revs-grid, .jdgm-medias-list-wrap, [data-jdgm-block="ugc_media_grid"]'
    );
    var mediaSection = null;
    if (mediaEl && ugcMediaCount() !== 0) {
      mediaSection = climbToShopifySection(mediaEl);
      if (mediaSection && !mediaSection.id) mediaSection.id = ensureUniqueId('photos', used);
      entries.push({ id: mediaSection.id, label: 'Photos', el: mediaSection });
    }

    /* Reviews widget. Target the review-list block specifically. The
     * broad `.jdgm-widget` matched the star-rating preview badge near
     * the top of the PDP first, sending the Reviews pill to the product
     * header instead of the reviews section. */
    var reviewsEl = document.querySelector('.jdgm-review-widget, [id*="review_widget"], .jdgm-rev-widg, .judgeme_product_reviews');
    if (reviewsEl && reviewCount() !== 0) {
      var reviewsSection = climbToShopifySection(reviewsEl);
      /* Defensive: if both blocks were ever merged into one apps
       * section, skip adding Reviews so we don't double-list it. */
      if (reviewsSection && reviewsSection !== mediaSection) {
        if (!reviewsSection.id) reviewsSection.id = ensureUniqueId('reviews', used);
        entries.push({ id: reviewsSection.id, label: 'Reviews', el: reviewsSection });
      }
    }
  }

  function populateToc(tocList, entries) {
    if (!tocList || !entries.length) return;
    tocList.innerHTML = '';
    entries.forEach(function (entry, i) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + entry.id;
      if (i === 0) a.className = 'is-active';
      a.setAttribute('data-bonpet-toc-link', entry.id);
      // Two labels: full (used on desktop sidebar) + short (used on
      // mobile pill rail). CSS toggles which is visible per viewport.
      var full = document.createElement('span');
      full.className = 'bonpet-pdp-toc-label-full';
      full.textContent = entry.label;
      var short = document.createElement('span');
      short.className = 'bonpet-pdp-toc-label-short';
      short.textContent = shortLabel(entry.label);
      a.appendChild(full);
      a.appendChild(short);
      li.appendChild(a);
      tocList.appendChild(li);
    });
  }

  /* Strip the leading "What it means:" / "Why it matters:" editorial
   * label from a body fragment. Handles every shape we've seen:
   *   <strong>🐮 What it means:</strong><br>body
   *   <strong>What it means</strong>: body                 (colon outside)
   *   🐮 What it means: <br>body                           (no wrap)
   *   <br>→ <strong>💚 Why it matters:</strong><br>body    (after leading <br>)
   *
   * The leading-chars class allows: whitespace, <br>, opening wrap tag
   * (strong/b/i/u/em/span), HTML entity, OR any non-word non-`<` char
   * (emoji, arrow, punctuation). We refuse to consume a letter or digit
   * before the label - that prevents body text like "At The Bon Pet..."
   * from false-matching when no label is present. */
  function stripEditorialLabel(html) {
    if (!html) return '';
    return html.replace(
      /^(?:\s|<br[^>]*>|<(?:strong|b|span|em|i|u)\b[^>]*>|&[a-z]+;|\W)*?(?:What it means|Why it matters)[\s\S]{0,30}?:\s*(?:<\/(?:strong|b|span|em|i|u)\s*>\s*)?(?:<br[^>]*>\s*)?\s*/i,
      ''
    );
  }

  /* Find the boundary inside a single <p> where "Why it matters" starts.
   * Required when the source packs both labels into one paragraph
   * separated by <br><br> (the fish page does this; pork uses two <p>s).
   * Returns the index of the leading <br> so the entire label line
   * is sliced into the accent fragment for label-stripping there.
   * Returns -1 when "Why it matters" isn't present. */
  function findAccentBoundary(html) {
    if (!html) return -1;
    var m = html.match(/(?:<br[^>]*>\s*)+[\s\S]{0,40}?Why it matters/i);
    return m ? m.index : -1;
  }

  /* Trim leading and trailing <br>s, &nbsp;s, and whitespace from a
   * fragment so the rendered paragraph doesn't carry empty leading
   * lines or stray trailing breaks. */
  function trimFragment(html) {
    if (!html) return '';
    return html
      .replace(/^(?:\s|<br[^>]*>|&nbsp;)+/gi, '')
      .replace(/(?:\s|<br[^>]*>|&nbsp;)+$/gi, '')
      .trim();
  }

  function wrapCardsInFirstSection(rte, firstH1) {
    if (!firstH1) return;
    // Walk siblings of the first h1 until we hit the next h1 or end.
    // Group consecutive h2 + (p|p) chains into .bonpet-pdp-card divs and
    // collect them under one .bonpet-pdp-card-grid wrapper inserted at
    // the position of the first such h2.
    var siblings = [];
    var cursor = firstH1.nextElementSibling;
    while (cursor && cursor.tagName !== 'H1') {
      siblings.push(cursor);
      cursor = cursor.nextElementSibling;
    }

    var cards = [];
    var i = 0;
    while (i < siblings.length) {
      var node = siblings[i];
      if (node.tagName === 'H2') {
        var card = document.createElement('article');
        card.className = 'bonpet-pdp-card';

        // Number badge: "01" + "/05". Index + total are zero-padded.
        // (idx is set after we know the total, below.)
        var numberWrap = document.createElement('div');
        numberWrap.className = 'bonpet-pdp-card-number';
        var numberMain = document.createElement('span');
        numberMain.className = 'bonpet-pdp-card-number-main';
        var numberFrac = document.createElement('span');
        numberFrac.className = 'bonpet-pdp-card-number-frac';
        numberWrap.appendChild(numberMain);
        numberWrap.appendChild(numberFrac);
        card.appendChild(numberWrap);

        // Title carries the source h2 text verbatim, including any leading
        // emoji. The number badge stands in for the icon-circle from the
        // prior iteration.
        var title = document.createElement('h2');
        title.className = 'bonpet-pdp-card-title';
        title.innerHTML = node.innerHTML;
        card.appendChild(title);

        // Collect every following <p> until next non-P sibling.
        var ps = [];
        var j = i + 1;
        while (j < siblings.length && siblings[j].tagName === 'P') {
          ps.push(siblings[j]);
          j += 1;
        }

        // Decompose into body + accent fragments. Two source shapes:
        //   - single <p> with both labels separated by <br><br>
        //   - two <p>s, one label each
        var bodyHtml = '';
        var accentHtml = '';
        if (ps.length === 1) {
          var html = ps[0].innerHTML;
          var splitIdx = findAccentBoundary(html);
          if (splitIdx >= 0) {
            bodyHtml = html.slice(0, splitIdx);
            accentHtml = html.slice(splitIdx);
          } else {
            bodyHtml = html;
          }
        } else if (ps.length >= 2) {
          bodyHtml = ps[0].innerHTML;
          accentHtml = ps.slice(1).map(function (p) { return p.innerHTML; }).join('<br><br>');
        }

        bodyHtml = trimFragment(stripEditorialLabel(bodyHtml));
        accentHtml = trimFragment(stripEditorialLabel(accentHtml));

        if (bodyHtml) {
          var bodyP = document.createElement('p');
          bodyP.className = 'bonpet-pdp-card-body';
          bodyP.innerHTML = bodyHtml;
          card.appendChild(bodyP);
        }
        if (accentHtml) {
          var accentP = document.createElement('p');
          accentP.className = 'bonpet-pdp-card-accent';
          accentP.innerHTML = accentHtml;
          card.appendChild(accentP);
        }

        cards.push({ card: card, numberMain: numberMain, numberFrac: numberFrac, replaces: siblings.slice(i, j) });
        i = j;
      } else {
        i += 1;
      }
    }

    if (!cards.length) return;

    // Fill in the number + fraction now that we know the total.
    var total = cards.length;
    var pad = function (n) { return n < 10 ? '0' + n : '' + n; };
    cards.forEach(function (entry, idx) {
      entry.numberMain.textContent = pad(idx + 1);
      entry.numberFrac.textContent = '/' + pad(total);
    });

    // Insert a single grid wrapper at the position of the first card's
    // original h2, then move every replaced sibling out of the DOM.
    var grid = document.createElement('div');
    grid.className = 'bonpet-pdp-card-grid';
    cards.forEach(function (entry) { grid.appendChild(entry.card); });

    // Append the CTA promo card (cloned from the <template> emitted by
    // sections/page.liquid). Defensive: silently skip when missing.
    var ctaTemplate = document.getElementById('bonpet-pdp-cta-template');
    if (ctaTemplate && ctaTemplate.content) {
      grid.appendChild(ctaTemplate.content.cloneNode(true));
    }

    var firstReplaced = cards[0].replaces[0];
    firstReplaced.parentNode.insertBefore(grid, firstReplaced);

    cards.forEach(function (entry) {
      entry.replaces.forEach(function (n) {
        if (n.parentNode) n.parentNode.removeChild(n);
      });
    });

    // Also remove the <hr> separators that originally divided the
    // subsections; the card chrome now carries the visual divide.
    grid.parentNode.querySelectorAll(':scope > hr').forEach(function (hr) {
      // Only remove hrs that sit between the first h1 and the next h1.
      var ref = hr;
      while (ref && ref.previousElementSibling) {
        ref = ref.previousElementSibling;
        if (ref.tagName === 'H1') {
          if (ref === firstH1) hr.parentNode.removeChild(hr);
          break;
        }
      }
    });
  }

  /* TOC behaviour: wire up click-to-smooth-scroll + intersection
   * scroll-spy. Both share `setActive` and `scrollLockUntil` so a
   * user click can preempt the observer for the duration of the
   * smooth-scroll animation. Previously the observer fired through
   * transient sections during a click-triggered scroll and stomped
   * the manually-selected toggle (e.g. clicking "Why" while on
   * "Ratio" briefly highlighted "Benefits" mid-scroll). */
  function wireTocBehavior(entries, tocList) {
    if (!tocList) return;

    var links = {};
    tocList.querySelectorAll('a[data-bonpet-toc-link]').forEach(function (a) {
      links[a.getAttribute('data-bonpet-toc-link')] = a;
    });

    function setActive(id) {
      Object.keys(links).forEach(function (k) {
        links[k].classList.toggle('is-active', k === id);
      });
    }

    // Timestamp until which scroll-spy observer fires should be ignored.
    // 800ms covers the typical smooth-scroll duration even for long jumps.
    var scrollLockUntil = 0;

    tocList.addEventListener('click', function (ev) {
      var a = ev.target.closest('a[data-bonpet-toc-link]');
      if (!a) return;
      var id = a.getAttribute('data-bonpet-toc-link');
      var target = document.getElementById(id);
      if (!target) return;
      ev.preventDefault();
      try {
        if (typeof history.replaceState === 'function') {
          history.replaceState(null, '', '#' + id);
        }
      } catch (e) { /* ignore */ }
      setActive(id);
      scrollLockUntil = Date.now() + 800;
      if (typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.location.hash = id;
      }
    });

    if (!entries.length || typeof IntersectionObserver !== 'function') return;

    var visible = {};
    var observer = new IntersectionObserver(function (records) {
      if (Date.now() < scrollLockUntil) return;
      records.forEach(function (r) {
        visible[r.target.id] = r.isIntersecting ? r.intersectionRatio : 0;
      });
      // Pick the topmost intersecting section.
      var pickId = null;
      var pickTop = Infinity;
      entries.forEach(function (entry) {
        if (visible[entry.id]) {
          var t = entry.el.getBoundingClientRect().top;
          if (t < pickTop) { pickTop = t; pickId = entry.id; }
        }
      });
      if (pickId) setActive(pickId);
    }, {
      rootMargin: '-30% 0px -60% 0px',
      threshold: [0, 0.1, 0.5, 1]
    });

    entries.forEach(function (entry) { observer.observe(entry.el); });

    /* Visibility envelope for the (fixed-position) toc: only show it
     * while the user is somewhere between the bottom of the MAIN
     * PRODUCT section and the bottom of the LAST tracked section.
     * Before that range it would overlap the product gallery / buy
     * box; after it would overlap You-may-also-like / Help strays /
     * footer.
     *
     * Implemented as a scroll listener (not IntersectionObserver)
     * because the observer's edge cases — initial state ordering,
     * missing <product-info> on some templates, partial intersection
     * thresholds with tall elements — kept producing false positives
     * during testing. A direct getBoundingClientRect check on every
     * scroll frame is unambiguous and trivial to debug. */
    var tocEl = tocList.closest('.bonpet-pdp-toc');
    var firstEntry = entries[0];
    var lastEntry = entries[entries.length - 1];
    if (tocEl && entries.length) {
      /* Initial hide until the first scroll handler tick runs. */
      tocEl.setAttribute('data-before-start', 'true');

      var productInfo = document.querySelector('product-info');
      var mainProductSection = productInfo ? productInfo.closest('[id^="shopify-section"]') : null;
      /* Fallback: use the first tracked section's top as the lower
       * bound if main-product can't be located. */
      var startGate = mainProductSection || (firstEntry && firstEntry.el);
      var endGate = lastEntry && lastEntry.el;

      var rafPending = false;
      function updateTocVisibility() {
        rafPending = false;
        if (!tocEl) return;
        var beforeStart = true;
        var pastEnd = false;
        if (startGate) {
          var rect = startGate.getBoundingClientRect();
          /* When the gate IS main-product: hide while any part of it
           * is on/below the viewport top. When the gate IS the first
           * tracked h1 (fallback): hide until its top crosses 0. */
          beforeStart = startGate === mainProductSection
            ? rect.bottom > 0
            : rect.top > 0;
        }
        if (endGate) {
          var rectEnd = endGate.getBoundingClientRect();
          /* Hide once the bottom of the last tracked section rises above
           * the bottom of the toc itself, not just the viewport top.
           * The toc is a tall fixed column; if we waited for the section
           * to clear the viewport top (<= 0), a short/empty Reviews
           * section lets the following sections (You-may-also-like, Help
           * strays, footer) scroll up into the toc's band while Reviews
           * is still technically on screen, so the toc floats over them.
           * Gating on the toc's own bottom hides it the moment the next
           * section would enter its vertical band. */
          var tocBottom = tocEl.getBoundingClientRect().bottom;
          pastEnd = rectEnd.bottom <= tocBottom;
        }
        tocEl.setAttribute('data-before-start', beforeStart ? 'true' : 'false');
        tocEl.setAttribute('data-past-end', pastEnd ? 'true' : 'false');
      }
      function onScroll() {
        if (rafPending) return;
        rafPending = true;
        requestAnimationFrame(updateTocVisibility);
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      updateTocVisibility();
    }
  }

  /* Wrap each editable table in the page-section rte inside a <details>
   * toggle so customers can collapse the long ingredient breakdown.
   * Summary label uses the actual row count ("View all N ingredients")
   * so it stays in sync if rows are added or removed in admin. The
   * first <tr> (header) is excluded from the count. Default open so
   * the table is visible on first paint. */
  function wrapTablesInToggle(rte) {
    var tables = rte.querySelectorAll(':scope > table');
    if (!tables.length) return;

    tables.forEach(function (table) {
      // Determine the preceding h1 to label the toggle appropriately.
      var prevH1 = table.previousElementSibling;
      while (prevH1 && prevH1.tagName !== 'H1') {
        prevH1 = prevH1.previousElementSibling;
      }
      var headingText = prevH1 ? (prevH1.textContent || '').toLowerCase() : '';

      // Count data rows. Header detection: first <tr> that contains any
      // <strong> descendant. Tag that row with a class so CSS can
      // target it explicitly (instead of `tr:first-child`, which
      // misfires when the table uses <thead>+<tbody> and matches the
      // first <tr> of EACH group).
      // NOTE: previous version used `seen[tr] = true` to dedupe across
      // overlapping selectors, which collapsed every row to one entry
      // because DOM elements all stringify to the same key.
      // `querySelectorAll('tr')` already returns each <tr> once, so no
      // dedup is needed.
      var trList = Array.prototype.slice.call(table.querySelectorAll('tr'));
      var dataRowCount = 0;
      var seenHeader = false;
      trList.forEach(function (tr) {
        if (!seenHeader && tr.querySelector('strong')) {
          tr.classList.add('bonpet-pdp-table-head');
          seenHeader = true;
          return;
        }
        dataRowCount += 1;
      });
      if (!seenHeader) {
        // No detectable header; assume all rows are data.
        dataRowCount = trList.length;
      }

      var noun = 'rows';
      if (/benefit/i.test(headingText)) noun = 'ingredients';
      else if (/ratio/i.test(headingText)) noun = 'ingredients';

      var details = document.createElement('details');
      details.className = 'bonpet-pdp-table-toggle';
      details.open = true;

      var summary = document.createElement('summary');
      summary.className = 'bonpet-pdp-table-toggle-summary';

      var label = document.createElement('span');
      label.className = 'bonpet-pdp-table-toggle-label';
      label.textContent = 'View all ' + dataRowCount + ' ' + noun;
      summary.appendChild(label);

      var icon = document.createElement('span');
      icon.className = 'bonpet-pdp-table-toggle-icon';
      icon.setAttribute('aria-hidden', 'true');
      summary.appendChild(icon);

      details.appendChild(summary);

      table.parentNode.insertBefore(details, table);
      details.appendChild(table);
    });
  }

  /* Per-rte transformation pipeline. Idempotent on first pass. Runs on
   * every .bonpet-pdp-rte found on the page - single-protein PDPs have
   * one (inside .bonpet-pdp-pane), trial-pack PDPs have N (one per
   * protein tab panel). */
  function processRte(rte) {
    normalizeReasonHeadings(rte);
    promoteLeadingH2(rte);
    var h1s = rte.querySelectorAll(':scope > h1');
    if (h1s.length) {
      wrapCardsInFirstSection(rte, h1s[0]);
    }
    wrapTablesInToggle(rte);
  }

  function init() {
    // Process every .bonpet-pdp-rte on the page (single-protein PDPs:
    // one rte inside .bonpet-pdp-pane; trial-pack PDPs: N rtes inside
    // .bonpet-trial-panel containers).
    var rtes = document.querySelectorAll('.bonpet-pdp-rte');
    if (!rtes.length) return;
    rtes.forEach(processRte);

    // Sidebar TOC + scroll-spy only run when a .bonpet-pdp-pane wraps
    // the rte (single-protein PDPs). Trial-pack PDPs use the tab bar
    // as their nav so the pane (and toc) aren't rendered.
    var pane = document.querySelector(PANE_SELECTOR);
    if (!pane) return;
    var rte = pane.querySelector('.bonpet-pdp-rte');
    if (!rte) return;
    var tocList = document.querySelector(TOC_SELECTOR);

    var used = {};
    var entries = tagH1sAndBuildToc(rte, tocList);
    entries.forEach(function (e) { used[e.id] = true; });

    appendExtraEntries(entries, used);
    populateToc(tocList, entries);

    wireTocBehavior(entries, tocList);

    /* Desktop sidebar + mobile rail both use position: fixed (so they
     * stay visible across the Judge.me sections, which sit outside
     * the page section's grid). Move the toc element to <body> first
     * so no ancestor's transform / will-change / filter / perspective
     * can create a containing block that traps fixed positioning
     * inside a section wrapper. (Common Dawn pitfall.) The 220px grid
     * column is still reserved in .bonpet-pdp-layout so the content
     * pane doesn't reflow.
     *
     * Then align the desktop sidebar's left edge to the grid
     * container's left so it visually overlays the reserved column.
     * Re-align on resize. Mobile uses CSS `left: 0` (full-width rail)
     * and skips the alignment. */
    var tocAside = tocList.closest('.bonpet-pdp-toc');
    var layoutEl = document.querySelector('.bonpet-pdp-layout');
    if (tocAside && document.body && tocAside.parentNode !== document.body) {
      document.body.appendChild(tocAside);
    }
    function alignDesktopTocPosition() {
      if (!tocAside || !layoutEl) return;
      if (window.matchMedia('(max-width: 899px)').matches) {
        tocAside.style.left = '';
        return;
      }
      var rect = layoutEl.getBoundingClientRect();
      tocAside.style.left = rect.left + 'px';
    }
    alignDesktopTocPosition();
    window.addEventListener('resize', alignDesktopTocPosition);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
